import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Eye, EyeOff, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { cn, formatMw, formatPrice, signClass } from "../lib/utils";
import {
  qhGateStatus,
  qhIntervalLabel,
  qhMinutesToGateClosure,
  type GateStatus,
} from "../lib/intervals";
import { useDisplay } from "../stores/display";
import { useGridChart } from "../stores/grid-chart";
import { useGridColumns, type ColId } from "../stores/grid-columns";
import { usePositionFilter } from "../stores/position-filter";
import { KeyValueList } from "../components/ui/key-value-list";
import type {
  GridCell,
  GridDeliveryDay as DeliveryDay,
  GridFreshness,
  GridPortfolio as Portfolio,
  GridSourceFreshness,
  GridResponse,
} from "../features/trading/grid/contracts";
import {
  buildGridRequestPath,
  gridQueryKeys,
} from "../features/trading/grid/queries";
import {
  GRID_CHART_EPSILON as CHART_EPS,
  bmSpread,
  bmTooltipLines,
  buildChartSeries,
  buildSelectionStats,
  computeTotals,
  daMarketPriceSourceLabel,
  daSourceLabel,
  effectiveBmMw,
  idBuyMw,
  idBuyVwap,
  idSellMw,
  idSellVwap,
  idTotalMw,
  idVwap,
  isFiniteNumber,
  lineSegments,
  makeTicks,
  makeXTicks,
  netVwap,
  paddedAbs,
  paddedRange,
  realizedPnl,
  tooltipPnlLines,
  type ChartBarSeries,
  type ChartPointSeries,
  type GridTotals,
  type SelectionPnlLine,
  type SelectionStatLine,
  type SelectionStats,
} from "../features/trading/grid/selectors";
export const Route = createFileRoute("/grid")({
  component: GridPage,
});

function GridPage() {
  const portfoliosQ = useQuery({
    queryKey: gridQueryKeys.portfolios(),
    queryFn: () => api<{ portfolios: Portfolio[] }>("/portfolios"),
  });
  const daysQ = useQuery({
    queryKey: gridQueryKeys.deliveryDays(),
    queryFn: () => api<{ days: DeliveryDay[] }>("/portfolios/delivery-days"),
  });

  const daPortfolios = useMemo(
    () => (portfoliosQ.data?.portfolios ?? []).filter((p) => p.market === "DA"),
    [portfoliosQ.data]
  );
  const idPortfolios = useMemo(
    () => (portfoliosQ.data?.portfolios ?? []).filter((p) => p.market === "ID"),
    [portfoliosQ.data]
  );

  const date = usePositionFilter((s) => s.deliveryDate);
  const setDate = usePositionFilter((s) => s.setDeliveryDate);
  const portfolioDa = usePositionFilter((s) => s.portfolioDa);
  const setPortfolioDa = usePositionFilter((s) => s.setPortfolioDa);
  const portfolioId = usePositionFilter((s) => s.portfolioId);
  const setPortfolioId = usePositionFilter((s) => s.setPortfolioId);
  const zone = useDisplay((s) => s.zone);
  const setZone = useDisplay((s) => s.setZone);

  const enabled = Boolean(date && (portfolioDa || portfolioId));
  const query = useQuery({
    queryKey: gridQueryKeys.positions({
      deliveryDate: date,
      portfolioDa,
      portfolioId,
    }),
    enabled,
    queryFn: () =>
      api<GridResponse>(
        buildGridRequestPath({ deliveryDate: date, portfolioDa, portfolioId })
      ),
  });

  const noPortfolios =
    !portfoliosQ.isLoading && (portfoliosQ.data?.portfolios.length ?? 0) === 0;

  return (
    <div className="min-w-0 space-y-4 p-3 sm:space-y-3 sm:p-4">
      <div className="grid min-w-0 grid-cols-1 gap-3 min-[480px]:grid-cols-2 sm:flex sm:flex-wrap sm:items-end">
        <Field label="Delivery date">
          <input
            list="delivery-days"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-11 w-full min-w-0 rounded border border-border bg-transparent px-3 text-base sm:h-auto sm:w-auto sm:px-2 sm:py-1 sm:text-sm"
          />
          <datalist id="delivery-days">
            {(daysQ.data?.days ?? []).map((d) => (
              <option key={d.deliveryDate} value={d.deliveryDate}>
                trades {d.tradeCount} · schedules {d.scheduleCount}
              </option>
            ))}
          </datalist>
        </Field>
        <Field label="DA portfolio">
          <PortfolioSelect
            value={portfolioDa}
            onChange={setPortfolioDa}
            options={daPortfolios}
            placeholder={daPortfolios.length ? "— select DA —" : "no DA portfolios"}
            disabled={daPortfolios.length === 0}
          />
        </Field>
        <Field label="ID portfolio">
          <PortfolioSelect
            value={portfolioId}
            onChange={setPortfolioId}
            options={idPortfolios}
            placeholder={idPortfolios.length ? "— select ID —" : "no ID portfolios"}
            disabled={idPortfolios.length === 0}
          />
        </Field>
        <Field label="Zone">
          <select
            value={zone}
            onChange={(e) => setZone(e.target.value as "CET" | "EET")}
            className="h-11 w-full min-w-0 rounded border border-border bg-transparent px-3 text-base sm:h-auto sm:w-auto sm:px-2 sm:py-1 sm:text-sm"
          >
            <option value="CET">CET</option>
            <option value="EET">EET</option>
          </select>
        </Field>
        <div className="col-span-full flex min-w-0 flex-col gap-1 text-xs text-muted min-[480px]:flex-row min-[480px]:flex-wrap min-[480px]:gap-x-3 sm:ml-auto sm:items-center">
          {query.data && (
            <span>
              DA from:{" "}
              <span className="text-fg">{daSourceLabel(query.data.daSource)}</span>
            </span>
          )}
          {query.data && (
            <span>
              DA price:{" "}
              <span className="text-fg">{daMarketPriceSourceLabel(query.data.daMarketPriceSource)}</span>
            </span>
          )}
          <span>
            FX:{" "}
            {query.data
              ? <>{query.data.fx.rate.toFixed(4)} ({query.data.fx.source}
                  {query.data.fx.asOfDate && <span className="sm:hidden"> · {query.data.fx.asOfDate}</span>})</>
              : "—"}
          </span>
          {query.data?.freshness.latestObservationAt && (
            <span className="sm:hidden">
              Latest source update:{" "}
              <span className="text-fg">
                {formatUtcTimestamp(query.data.freshness.latestObservationAt)}
              </span>
            </span>
          )}
        </div>
      </div>

      {query.data && <GridFreshnessDetails freshness={query.data.freshness} />}

      {noPortfolios && (
        <div className="text-muted text-sm border border-border/60 rounded p-3">
          No portfolios in the database yet. Upload a trades file or DAM schedule on the{" "}
          <Link to="/ingest" className="text-accent underline">
            Ingest
          </Link>{" "}
          page to create them automatically.
        </div>
      )}

      {!noPortfolios && !enabled && (
        <div className="text-muted text-sm">
          Pick a delivery date and at least one portfolio.
        </div>
      )}
      {query.isLoading && <div className="text-muted text-sm">Loading…</div>}
      {query.isError && (
        <div className="text-negRed text-sm">{(query.error as Error).message}</div>
      )}
      {query.data && (
        <>
          <div className="hidden sm:block">
            <PositionChart
              rows={query.data.grid}
              zone={zone}
              deliveryDate={query.data.deliveryDate}
            />
          </div>
          <GridTable
            rows={query.data.grid}
            zone={zone}
            deliveryDate={query.data.deliveryDate}
          />
        </>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-xs text-muted">
      {label}
      {children}
    </label>
  );
}

function PortfolioSelect({
  value,
  onChange,
  options,
  placeholder,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  options: Portfolio[];
  placeholder: string;
  disabled?: boolean;
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className="h-11 w-full min-w-0 rounded border border-border bg-transparent px-3 text-base disabled:opacity-50 sm:h-auto sm:w-auto sm:min-w-[18rem] sm:px-2 sm:py-1 sm:text-sm"
    >
      <option value="">{placeholder}</option>
      {options.map((p) => (
        <option key={p.id} value={p.id}>
          {labelFor(p)}
        </option>
      ))}
    </select>
  );
}

function labelFor(p: Portfolio): string {
  const owner = p.ownerDisplayName ?? p.ownerEmail ?? "?";
  const short = p.shortName ? ` — ${p.shortName}` : "";
  return `${owner} · ${p.market} · ${p.brokerCode}${short}`;
}

function formatUtcTimestamp(value: string): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return `${date.toLocaleString("en-GB", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })} UTC`;
}

function GridFreshnessDetails({ freshness }: { freshness: GridFreshness }) {
  const sources: Array<[string, GridSourceFreshness]> = [
    ["DA position", freshness.sources.daPosition],
    ["DA price · schedule", freshness.sources.daMarketPrice.components.schedule],
    ["DA price · BRMDA trades", freshness.sources.daMarketPrice.components.brmdaTrades],
    ["DA price · OPCOM reference", freshness.sources.daMarketPrice.components.opcomDam],
    ["Intraday trades", freshness.sources.intradayTrades],
    ["Balancing · final", freshness.sources.balancing.final],
    ["Balancing · estimated", freshness.sources.balancing.estimated],
  ];

  return (
    <details className="rounded-md border border-border/60 sm:hidden">
      <summary className="flex min-h-11 cursor-pointer touch-manipulation items-center px-3 py-2 text-sm font-medium">
        Source freshness
      </summary>
      <dl className="space-y-3 border-t border-border/40 p-3">
        {sources.map(([label, source]) => (
          <div key={label} className="min-w-0">
            <dt className="text-xs font-medium text-fg">{label}</dt>
            <dd className="break-words text-xs text-muted">
              {source.status} · {source.observationCount} observation
              {source.observationCount === 1 ? "" : "s"}
              {source.asOf ? ` · as of ${formatUtcTimestamp(source.asOf)}` : " · as-of unavailable"}
            </dd>
          </div>
        ))}
      </dl>
    </details>
  );
}

type ChartHover = {
  rowIndex: number;
  left: number;
  top: number;
};

type SelectionDrag = {
  startIndex: number;
  endIndex: number;
  mode: "add" | "remove";
};

const CHART_WIDTH = 1200;
const CHART_HEIGHT = 430;
const CHART_LEFT = 58;
const CHART_RIGHT = 1134;
const PRICE_PLOT_TOP = 24;
const PRICE_PLOT_HEIGHT = 250;
const PRICE_PLOT_BOTTOM = PRICE_PLOT_TOP + PRICE_PLOT_HEIGHT;
const QTY_PLOT_TOP = 306;
const QTY_PLOT_HEIGHT = 86;
const QTY_PLOT_BOTTOM = QTY_PLOT_TOP + QTY_PLOT_HEIGHT;
function PositionChart({
  rows,
  zone,
  deliveryDate,
}: {
  rows: GridCell[];
  zone: "CET" | "EET";
  deliveryDate: string;
}) {
  const visible = useGridChart((s) => s.visible);
  const idmMode = useGridChart((s) => s.idmMode);
  const showBm = useGridChart((s) => s.showBm);
  const toggleVisible = useGridChart((s) => s.toggleVisible);
  const setIdmMode = useGridChart((s) => s.setIdmMode);
  const toggleBm = useGridChart((s) => s.toggleBm);
  const [nowTick, setNowTick] = useState(() => Date.now());
  const [hover, setHover] = useState<ChartHover | null>(null);
  const [selectedQhs, setSelectedQhs] = useState<Set<number>>(() => new Set());
  const [dragSelect, setDragSelect] = useState<SelectionDrag | null>(null);
  const chartBoxRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    setSelectedQhs(new Set());
    setDragSelect(null);
  }, [deliveryDate]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSelectedQhs(new Set());
        setDragSelect(null);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  useEffect(() => {
    if (!dragSelect) return;
    const stopDragging = () => setDragSelect(null);
    window.addEventListener("mouseup", stopDragging);
    return () => window.removeEventListener("mouseup", stopDragging);
  }, [dragSelect]);

  const now = useMemo(() => new Date(nowTick), [nowTick]);
  const rowStatus = useMemo(() => {
    const map = new Map<number, GateStatus>();
    for (const r of rows) map.set(r.qhIndex, qhGateStatus(deliveryDate, r.qhIndex, now));
    return map;
  }, [rows, deliveryDate, now]);

  const selectedRows = useMemo(
    () => rows.filter((r) => selectedQhs.has(r.qhIndex)),
    [rows, selectedQhs]
  );
  const selectionStats = useMemo(
    () => buildSelectionStats(selectedRows, rowStatus, zone),
    [selectedRows, rowStatus, zone]
  );

  const { priceSeries, barSeries } = useMemo(
    () => buildChartSeries(rows, idmMode, showBm, rowStatus),
    [rows, idmMode, showBm, rowStatus]
  );

  if (!visible) {
    return (
      <div className="border border-border/60 rounded-md px-3 py-2 flex items-center justify-between gap-3">
        <div className="text-xs font-medium">Market chart</div>
        <button
          type="button"
          onClick={toggleVisible}
          className={chartButtonClass(false)}
        >
          <Eye className="h-3.5 w-3.5" />
          Show
        </button>
      </div>
    );
  }

  const priceValues = priceSeries.flatMap((s) => s.values.filter(isFiniteNumber));
  const priceRange = paddedRange(priceValues);
  const quantityValues = barSeries.flatMap((s) => s.values);
  const maxAbsQuantity = paddedAbs(Math.max(1, ...quantityValues.map((v) => Math.abs(v))));
  const plotWidth = CHART_RIGHT - CHART_LEFT;
  const step = rows.length > 0 ? plotWidth / rows.length : plotWidth;
  const xForIndex = (idx: number) => CHART_LEFT + (idx + 0.5) * step;
  const yForPrice = (value: number) =>
    PRICE_PLOT_TOP +
    ((priceRange.max - value) / (priceRange.max - priceRange.min)) * PRICE_PLOT_HEIGHT;
  const yForQuantity = (value: number) =>
    QTY_PLOT_TOP + ((maxAbsQuantity - value) / (maxAbsQuantity * 2)) * QTY_PLOT_HEIGHT;
  const yZero = yForQuantity(0);
  const priceTicks = makeTicks(priceRange.min, priceRange.max, 4);
  const xTicks = makeXTicks(rows, zone);
  const quantityTicks = [maxAbsQuantity, 0, -maxAbsQuantity];
  const barSlot = step * 0.86;
  const barWidth = Math.max(1.2, Math.min(4.5, barSlot / Math.max(1, barSeries.length) - 0.45));
  const maxOpenExposureMw = Math.max(
    1,
    ...rows
      .filter((r) => rowStatus.get(r.qhIndex) !== "closed" && Math.abs(r.netMw) > CHART_EPS)
      .map((r) => Math.abs(r.netMw))
  );
  const hoveredRow = hover ? rows[hover.rowIndex] : undefined;
  const setIntervalHover = (
    rowIndex: number,
    e: React.MouseEvent<SVGRectElement>
  ) => {
    const rect = chartBoxRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = 300;
    const height = 280;
    const localX = e.clientX - rect.left;
    const localY = e.clientY - rect.top;
    const left = clamp(localX + 14, 8, Math.max(8, rect.width - width - 8));
    const top = clamp(localY + 14, 8, Math.max(8, rect.height - height - 8));
    setHover({ rowIndex, left, top });
  };
  const applySelectionRange = (
    startIndex: number,
    endIndex: number,
    mode: SelectionDrag["mode"]
  ) => {
    const min = Math.min(startIndex, endIndex);
    const max = Math.max(startIndex, endIndex);
    setSelectedQhs((prev) => {
      const next = new Set(prev);
      for (let i = min; i <= max; i++) {
        const row = rows[i];
        if (!row) continue;
        if (mode === "add") next.add(row.qhIndex);
        else next.delete(row.qhIndex);
      }
      return next;
    });
  };
  const beginSelection = (rowIndex: number, e: React.MouseEvent<SVGRectElement>) => {
    e.preventDefault();
    const row = rows[rowIndex];
    if (!row) return;
    const mode: SelectionDrag["mode"] = selectedQhs.has(row.qhIndex) ? "remove" : "add";
    setDragSelect({ startIndex: rowIndex, endIndex: rowIndex, mode });
    applySelectionRange(rowIndex, rowIndex, mode);
  };
  const extendSelection = (rowIndex: number, e: React.MouseEvent<SVGRectElement>) => {
    setIntervalHover(rowIndex, e);
    if (!dragSelect || dragSelect.endIndex === rowIndex) return;
    applySelectionRange(dragSelect.startIndex, rowIndex, dragSelect.mode);
    setDragSelect({ ...dragSelect, endIndex: rowIndex });
  };
  const clearSelection = () => {
    setSelectedQhs(new Set());
    setDragSelect(null);
  };

  return (
    <div className="border border-border/60 rounded-md bg-bg">
      <div className="px-3 py-2 border-b border-border/50 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <div className="text-xs font-medium">Market chart</div>
          <ChartLegend priceSeries={priceSeries} barSeries={barSeries} />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <div className="inline-flex overflow-hidden rounded border border-border/70">
            <button
              type="button"
              onClick={() => setIdmMode("net")}
              className={chartSegmentClass(idmMode === "net")}
            >
              IDM Net
            </button>
            <button
              type="button"
              onClick={() => setIdmMode("split")}
              className={chartSegmentClass(idmMode === "split")}
            >
              IDM Buy/Sell
            </button>
          </div>
          <button
            type="button"
            onClick={toggleBm}
            className={chartButtonClass(showBm)}
          >
            BM prices
          </button>
          <button
            type="button"
            onClick={toggleVisible}
            className={chartButtonClass(false)}
          >
            <EyeOff className="h-3.5 w-3.5" />
            Hide
          </button>
        </div>
      </div>

      <div className="grid gap-2 p-2 lg:grid-cols-[minmax(0,1fr)_18rem]">
        <div
          ref={chartBoxRef}
          className="relative min-w-0"
          onMouseLeave={() => setHover(null)}
        >
          <svg
            viewBox={`0 0 ${CHART_WIDTH} ${CHART_HEIGHT}`}
            role="img"
            aria-label="Position and P&L market price and quantity chart"
            className="h-[27rem] w-full select-none"
          >
            <rect
              x={CHART_LEFT}
              y={PRICE_PLOT_TOP}
              width={plotWidth}
              height={PRICE_PLOT_HEIGHT}
              fill="rgb(var(--border))"
              opacity="0.035"
              rx="4"
            />
            <rect
              x={CHART_LEFT}
              y={QTY_PLOT_TOP}
              width={plotWidth}
              height={QTY_PLOT_HEIGHT}
              fill="rgb(var(--border))"
              opacity="0.035"
              rx="4"
            />

          {priceTicks.map((tick) => {
            const y = yForPrice(tick);
            return (
              <g key={`price-tick-${tick}`}>
                <line
                  x1={CHART_LEFT}
                  x2={CHART_RIGHT}
                  y1={y}
                  y2={y}
                  stroke="rgb(var(--border))"
                  strokeOpacity="0.55"
                  strokeWidth="1"
                />
                <text
                  x={CHART_LEFT - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="rgb(var(--muted))"
                  fontSize="11"
                >
                  {formatAxisNumber(tick)}
                </text>
              </g>
            );
          })}

          {quantityTicks.map((tick) => {
            const y = yForQuantity(tick);
            return (
              <g key={`qty-tick-${tick}`}>
                <line
                  x1={CHART_LEFT}
                  x2={CHART_RIGHT}
                  y1={y}
                  y2={y}
                  stroke="rgb(var(--border))"
                  strokeOpacity={tick === 0 ? "0.85" : "0.18"}
                  strokeWidth={tick === 0 ? "1.4" : "1"}
                  strokeDasharray={tick === 0 ? undefined : "4 6"}
                />
                <text
                  x={CHART_RIGHT + 8}
                  y={y}
                  textAnchor="start"
                  dominantBaseline="middle"
                  fill="rgb(var(--muted))"
                  fontSize="11"
                >
                  {formatAxisMw(tick)}
                </text>
              </g>
            );
          })}

          {xTicks.map(({ idx, label }) => {
            const x = xForIndex(idx);
            return (
              <g key={`x-tick-${idx}`}>
                <line
                  x1={x}
                  x2={x}
                  y1={PRICE_PLOT_TOP}
                  y2={QTY_PLOT_BOTTOM}
                  stroke="rgb(var(--border))"
                  strokeOpacity="0.22"
                  strokeWidth="1"
                />
                <text
                  x={x}
                  y={CHART_HEIGHT - 14}
                  textAnchor="middle"
                  fill="rgb(var(--muted))"
                  fontSize="11"
                >
                  {label}
                </text>
              </g>
            );
          })}

          <line
            x1={CHART_LEFT}
            x2={CHART_RIGHT}
            y1={QTY_PLOT_TOP - 16}
            y2={QTY_PLOT_TOP - 16}
            stroke="rgb(var(--border))"
            strokeOpacity="0.45"
          />

          <text x="12" y={PRICE_PLOT_TOP + 12} fill="rgb(var(--muted))" fontSize="11">
            €/MWh
          </text>
          <text x={CHART_RIGHT + 12} y={QTY_PLOT_TOP + 12} fill="rgb(var(--muted))" fontSize="11">
            MW
          </text>

          {rows.map((row, idx) => {
            const gateStatus = rowStatus.get(row.qhIndex) ?? "open";
            if (gateStatus === "closed" || Math.abs(row.netMw) < CHART_EPS) return null;
            const color = openExposureColor(row.netMw);
            const x = CHART_LEFT + idx * step;
            return (
              <g key={`open-exposure-${row.qhIndex}`} pointerEvents="none">
                <rect
                  x={x}
                  y={QTY_PLOT_TOP}
                  width={step}
                  height={QTY_PLOT_HEIGHT}
                  fill={color}
                  opacity={openExposureOpacity(row.netMw, maxOpenExposureMw, gateStatus)}
                />
                {gateStatus === "closing" && (
                  <line
                    x1={x + 0.7}
                    x2={x + step - 0.7}
                    y1={QTY_PLOT_BOTTOM - 1}
                    y2={QTY_PLOT_BOTTOM - 1}
                    stroke={color}
                    strokeOpacity="0.58"
                    strokeWidth="2"
                  />
                )}
              </g>
            );
          })}

          {rows.map((row, idx) => {
            if (!selectedQhs.has(row.qhIndex)) return null;
            const x = CHART_LEFT + idx * step;
            return (
              <g key={`selected-interval-${row.qhIndex}`} pointerEvents="none">
                <rect
                  x={x}
                  y={PRICE_PLOT_TOP}
                  width={step}
                  height={QTY_PLOT_BOTTOM - PRICE_PLOT_TOP}
                  fill="rgb(var(--accent))"
                  opacity="0.08"
                />
                <rect
                  x={x + 0.6}
                  y={PRICE_PLOT_TOP + 0.6}
                  width={Math.max(0, step - 1.2)}
                  height={QTY_PLOT_BOTTOM - PRICE_PLOT_TOP - 1.2}
                  fill="none"
                  stroke="rgb(var(--accent))"
                  strokeOpacity="0.55"
                  strokeWidth="1.2"
                />
              </g>
            );
          })}

          {hover && hoveredRow && (
            <rect
              x={CHART_LEFT + hover.rowIndex * step}
              y={PRICE_PLOT_TOP}
              width={step}
              height={QTY_PLOT_BOTTOM - PRICE_PLOT_TOP}
              fill="rgb(var(--pos-green))"
              opacity="0.14"
              pointerEvents="none"
            />
          )}

          {barSeries.map((series, seriesIndex) =>
            series.values.map((value, rowIndex) => {
              if (Math.abs(value) < CHART_EPS) return null;
              const row = rows[rowIndex];
              if (!row) return null;
              const x =
                xForIndex(rowIndex) -
                barSlot / 2 +
                seriesIndex * (barSlot / Math.max(1, barSeries.length)) +
                (barSlot / Math.max(1, barSeries.length) - barWidth) / 2;
              const y = value >= 0 ? yForQuantity(value) : yZero;
              const height = Math.abs(yForQuantity(value) - yZero);
              return (
                <rect
                  key={`${series.id}-${row.qhIndex}`}
                  x={x}
                  y={y}
                  width={barWidth}
                  height={Math.max(1, height)}
                  rx="0.8"
                  fill={series.color}
                  opacity={series.opacity}
                />
              );
            })
          )}

          {priceSeries.map((series) =>
            lineSegments(series.values, xForIndex, yForPrice).map((path, idx) => (
              <path
                key={`${series.id}-line-${idx}`}
                d={path}
                fill="none"
                stroke={series.color}
                strokeWidth={series.strokeWidth ?? 2}
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeDasharray={series.strokeDasharray}
              />
            ))
          )}

          {priceSeries.map((series) =>
            series.pointRadius
              ? series.values.map((value, idx) => {
                  if (!isFiniteNumber(value)) return null;
                  const row = rows[idx];
                  if (!row) return null;
                  return (
                    <circle
                      key={`${series.id}-point-${row.qhIndex}`}
                      cx={xForIndex(idx)}
                      cy={yForPrice(value)}
                      r={series.pointRadius}
                      fill={series.color}
                      opacity="0.7"
                    />
                  );
                })
              : null
          )}

          {showBm &&
            rows.map((row, idx) => {
              if (Math.abs(row.bmNetMw) < CHART_EPS) return null;
              if (rowStatus.get(row.qhIndex) !== "closed") return null;
              if (!isFiniteNumber(row.bmAppliedPriceEur)) return null;
              return (
                <circle
                  key={`bm-closed-${row.qhIndex}`}
                  cx={xForIndex(idx)}
                  cy={yForPrice(row.bmAppliedPriceEur)}
                  r="4.6"
                  fill="rgb(var(--neg-red))"
                  stroke="rgb(var(--bg))"
                  strokeWidth="2"
                />
              );
            })}

          {rows.map((row, idx) => (
            <rect
              key={`hover-band-${row.qhIndex}`}
              x={CHART_LEFT + idx * step}
              y={PRICE_PLOT_TOP}
              width={step}
              height={QTY_PLOT_BOTTOM - PRICE_PLOT_TOP}
              fill="transparent"
              pointerEvents="all"
              onMouseDown={(e) => beginSelection(idx, e)}
              onMouseMove={(e) => extendSelection(idx, e)}
              onMouseEnter={(e) => extendSelection(idx, e)}
            />
          ))}
          </svg>

          {hover && hoveredRow && (
            <ChartTooltip
              row={hoveredRow}
              zone={zone}
              gateStatus={rowStatus.get(hoveredRow.qhIndex) ?? "open"}
              style={{ left: hover.left, top: hover.top }}
            />
          )}
        </div>
        <SelectionStatsPanel stats={selectionStats} onClear={clearSelection} />
      </div>
    </div>
  );
}

function ChartTooltip({
  row,
  zone,
  gateStatus,
  style,
}: {
  row: GridCell;
  zone: "CET" | "EET";
  gateStatus: GateStatus;
  style: React.CSSProperties;
}) {
  const netPrice = netVwap(row);
  const idBuy = idBuyMw(row);
  const idSell = idSellMw(row);
  const hasIdBuy = Math.abs(idBuy) > CHART_EPS;
  const hasIdSell = Math.abs(idSell) > CHART_EPS;
  const showIdNet = hasIdBuy && hasIdSell;
  const bmMw = effectiveBmMw(row, gateStatus);
  const bmLines = bmTooltipLines(row, bmMw);
  const pnlLines = tooltipPnlLines(row, gateStatus);
  return (
    <div
      className="pointer-events-none absolute z-30 w-[300px] max-w-[calc(100%-16px)] rounded-md border border-border/80 bg-bg/95 px-3 py-2 text-[11px] shadow-lg backdrop-blur"
      style={style}
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 border-b border-border/50 pb-1 text-muted">
        <span>QH-{row.qhIndex}</span>
        <span>{qhIntervalLabel(row.qhIndex, zone)}</span>
      </div>
      <TooltipLine label="DA" mw={row.daBaseMw} price={row.daMarketPriceEur} strong />
      {hasIdBuy && <TooltipLine label="IDM Buy" mw={idBuy} price={idBuyVwap(row)} strong={!hasIdSell} />}
      {hasIdSell && <TooltipLine label="IDM Sell" mw={idSell} price={idSellVwap(row)} strong={!hasIdBuy} />}
      {showIdNet && <TooltipLine label="IDM Net" mw={idTotalMw(row)} price={idVwap(row)} strong />}
      <div className="my-1.5 border-t border-border/40" />
      {Math.abs(row.netMw) > CHART_EPS && <TooltipLine label="NET DA+IDM" mw={row.netMw} price={netPrice} strong />}
      {bmLines.map((line) => (
        <TooltipLine
          key={line.label}
          label={line.label}
          mw={bmMw}
          price={line.price}
          strong={line.strong}
        />
      ))}
      {pnlLines.length > 0 && (
        <div className="mt-1.5 border-t border-border/40 pt-1.5">
          <div className="mb-0.5 text-[10px] uppercase tracking-wider text-muted">P&L</div>
          {pnlLines.map((line) => (
            <TooltipPnlLine
              key={line.label}
              label={line.label}
              value={line.value}
              strong={line.strong}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TooltipLine({
  label,
  mw,
  price,
  strong = false,
}: {
  label: string;
  mw: number;
  price: number | null;
  strong?: boolean;
}) {
  return (
    <div className={cn("grid grid-cols-[5.75rem_1fr] items-center gap-3", strong && "font-semibold")}>
      <span className="text-muted">{label}</span>
      <span className="text-right font-mono text-fg">
        {formatMw(mw)} MW @ {formatPrice(price)} €/MWh
      </span>
    </div>
  );
}

function TooltipPnlLine({
  label,
  value,
  strong = false,
}: {
  label: string;
  value: number;
  strong?: boolean;
}) {
  return (
    <div className={cn("grid grid-cols-[5.75rem_1fr] items-center gap-3", strong && "font-semibold")}>
      <span className="text-muted">{label}</span>
      <span className={cn("text-right font-mono", signClass(value))}>
        {formatSignedMoney(value)}
      </span>
    </div>
  );
}

function formatSignedMoney(value: number): string {
  const normalized = Math.abs(value) < CHART_EPS ? 0 : value;
  const prefix = normalized > 0 ? "+" : "";
  return `${prefix}${formatPrice(normalized)} €`;
}

function SelectionStatsPanel({
  stats,
  onClear,
}: {
  stats: SelectionStats | null;
  onClear: () => void;
}) {
  return (
    <aside className="min-w-0 border-t border-border/50 pt-2 text-[11px] lg:border-l lg:border-t-0 lg:pl-3 lg:pt-0">
      <div className="mb-2 flex items-start justify-between gap-2 border-b border-border/40 pb-1.5">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-muted">Selection</div>
          <div
            className="truncate font-mono text-xs text-fg"
            title={stats?.rangeLabel}
          >
            {stats ? `${stats.qhCount} QHs · ${stats.rangeLabel}` : "0 QHs"}
          </div>
        </div>
        {stats && (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded border border-border/70 text-muted hover:border-accent/70 hover:text-fg"
            title="clear selection"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {stats && (
        <div className="space-y-1.5 font-mono">
          {stats.lines.map((line) => (
            <SelectionStatLineView key={line.label} line={line} />
          ))}
          {stats.pnlLines.length > 0 && (
            <div className="mt-2 border-t border-border/40 pt-1.5">
              <div className="mb-0.5 font-sans text-[10px] uppercase tracking-wider text-muted">P&L</div>
              {stats.pnlLines.map((line) => (
                <SelectionPnlLineView key={line.label} line={line} />
              ))}
            </div>
          )}
        </div>
      )}
    </aside>
  );
}

function SelectionStatLineView({ line }: { line: SelectionStatLine }) {
  return (
    <div className={cn("grid grid-cols-[5.75rem_1fr] items-center gap-2", line.strong && "font-semibold")}>
      <span className="font-sans text-muted">{line.label}</span>
      <span className="text-right text-fg">
        {formatMw(line.quantityMwh)} MWh @ {formatPrice(line.price)} €/MWh
      </span>
    </div>
  );
}

function SelectionPnlLineView({ line }: { line: SelectionPnlLine }) {
  return (
    <div className={cn("grid grid-cols-[5.75rem_1fr] items-center gap-2", line.strong && "font-semibold")}>
      <span className="font-sans text-muted">{line.label}</span>
      <span className={cn("text-right", signClass(line.value))}>{formatSignedMoney(line.value)}</span>
    </div>
  );
}

function ChartLegend({
  priceSeries,
  barSeries,
}: {
  priceSeries: ChartPointSeries[];
  barSeries: ChartBarSeries[];
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted">
      {priceSeries.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1.5">
          <span className="h-px w-5" style={{ backgroundColor: s.color }} />
          {s.label}
        </span>
      ))}
      {barSeries.map((s) => (
        <span key={s.id} className="inline-flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-[1px]" style={{ backgroundColor: s.color, opacity: s.opacity }} />
          {s.label} MW
        </span>
      ))}
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-4 rounded-[1px]" style={{ backgroundColor: OPEN_LONG_COLOR, opacity: 0.18 }} />
        open long
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="h-2 w-4 rounded-[1px]" style={{ backgroundColor: OPEN_SHORT_COLOR, opacity: 0.18 }} />
        open short
      </span>
    </div>
  );
}

function chartButtonClass(active: boolean): string {
  return cn(
    "inline-flex h-7 items-center gap-1.5 rounded border px-2 text-xs transition-colors",
    active
      ? "border-accent/70 bg-accent/10 text-fg"
      : "border-border/70 text-muted hover:border-accent/70 hover:text-fg"
  );
}

function chartSegmentClass(active: boolean): string {
  return cn(
    "h-7 px-2 transition-colors border-r border-border/70 last:border-r-0",
    active ? "bg-posGreen/10 text-fg" : "text-muted hover:text-fg hover:bg-border/20"
  );
}

const OPEN_LONG_COLOR = "rgb(14, 165, 233)";
const OPEN_SHORT_COLOR = "rgb(245, 158, 11)";

function openExposureColor(netMw: number): string {
  return netMw > 0 ? OPEN_LONG_COLOR : OPEN_SHORT_COLOR;
}

function openExposureOpacity(netMw: number, maxAbsMw: number, gateStatus: GateStatus): number {
  const normalized = clamp(Math.abs(netMw) / Math.max(1, maxAbsMw), 0, 1);
  const opacity = 0.05 + Math.sqrt(normalized) * 0.14 + (gateStatus === "closing" ? 0.05 : 0);
  return clamp(opacity, 0.05, 0.24);
}

function formatAxisNumber(value: number): string {
  const abs = Math.abs(value);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return value.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function formatAxisMw(value: number): string {
  return value.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: Math.abs(value) >= 10 ? 0 : 1,
  });
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

type ColGroup = "time" | "da" | "id" | "net" | "balancing";

interface RenderCtx {
  status?: GateStatus;
  minsToClose?: number;
}

interface ColDef {
  id: ColId;
  label: (zone: "CET" | "EET") => string;
  title?: (zone: "CET" | "EET") => string;
  group: ColGroup;
  align: "left" | "right";
  render(r: GridCell, zone: "CET" | "EET", ctx: RenderCtx): React.ReactNode;
  cellClass?(r: GridCell, ctx: RenderCtx): string;
}

const GROUP_STYLE: Record<ColGroup, { label: string; text: string; headerBg: string; cellBg: string }> = {
  time: { label: "Time", text: "text-muted", headerBg: "", cellBg: "" },
  da: { label: "Day-Ahead", text: "text-accent", headerBg: "bg-accent/10", cellBg: "bg-accent/5" },
  id: { label: "Intraday (BRMID)", text: "text-posGreen", headerBg: "bg-posGreen/10", cellBg: "bg-posGreen/5" },
  net: { label: "Net", text: "text-fg", headerBg: "bg-border/20", cellBg: "bg-border/10" },
  balancing: { label: "Balancing", text: "text-warnOrange", headerBg: "bg-warnOrange/10", cellBg: "bg-warnOrange/5" },
};

const COLS: Record<ColId, ColDef> = {
  qh: {
    id: "qh",
    label: () => "QH",
    group: "time",
    align: "left",
    render: (r) => `QH-${r.qhIndex}`,
  },
  interval: {
    id: "interval",
    label: (z) => `Interval (${z})`,
    group: "time",
    align: "left",
    render: (r, z) => qhIntervalLabel(r.qhIndex, z),
  },
  status: {
    id: "status",
    label: () => "Status",
    group: "time",
    align: "left",
    render: (_r, _z, ctx) => <StatusBadge status={ctx.status} minsToClose={ctx.minsToClose} />,
  },
  daPriceEur: {
    id: "daPriceEur",
    label: () => "DA €/MWh",
    group: "da",
    align: "right",
    render: (r) => formatPrice(r.daMarketPriceEur ?? r.daPriceEur),
  },
  daBaseMw: {
    id: "daBaseMw",
    label: () => "DA MW",
    group: "da",
    align: "right",
    render: (r) => formatMw(r.daBaseMw),
    cellClass: (r) => signClass(r.daBaseMw),
  },
  qhBuyMw: {
    id: "qhBuyMw",
    label: () => "Q Buy MW",
    group: "id",
    align: "right",
    render: (r) => formatMw(r.qhBuyMw),
    cellClass: (r) => signClass(r.qhBuyMw),
  },
  qhBuyVwap: {
    id: "qhBuyVwap",
    label: () => "QB €/MWh",
    group: "id",
    align: "right",
    render: (r) => formatPrice(r.qhBuyVwap),
    cellClass: () => "text-muted",
  },
  qhSellMw: {
    id: "qhSellMw",
    label: () => "Q Sell MW",
    group: "id",
    align: "right",
    render: (r) => formatMw(r.qhSellMw),
    cellClass: (r) => signClass(r.qhSellMw),
  },
  qhSellVwap: {
    id: "qhSellVwap",
    label: () => "QS €/MWh",
    group: "id",
    align: "right",
    render: (r) => formatPrice(r.qhSellVwap),
    cellClass: () => "text-muted",
  },
  phBuyMw: {
    id: "phBuyMw",
    label: () => "H Buy MW",
    group: "id",
    align: "right",
    render: (r) => formatMw(r.phBuyMw),
    cellClass: (r) => signClass(r.phBuyMw),
  },
  phBuyVwap: {
    id: "phBuyVwap",
    label: () => "HB €/MWh",
    group: "id",
    align: "right",
    render: (r) => formatPrice(r.phBuyVwap),
    cellClass: () => "text-muted",
  },
  phSellMw: {
    id: "phSellMw",
    label: () => "H Sell MW",
    group: "id",
    align: "right",
    render: (r) => formatMw(r.phSellMw),
    cellClass: (r) => signClass(r.phSellMw),
  },
  phSellVwap: {
    id: "phSellVwap",
    label: () => "HS €/MWh",
    group: "id",
    align: "right",
    render: (r) => formatPrice(r.phSellVwap),
    cellClass: () => "text-muted",
  },
  idTotalMw: {
    id: "idTotalMw",
    label: () => "ID Total MW",
    group: "id",
    align: "right",
    render: (r) => formatMw(idTotalMw(r)),
    cellClass: (r) => cn("font-bold", signClass(idTotalMw(r))),
  },
  idVwapEur: {
    id: "idVwapEur",
    label: () => "ID €/MWh",
    group: "id",
    align: "right",
    render: (r) => formatPrice(idVwap(r)),
    cellClass: () => "font-bold",
  },
  netMw: {
    id: "netMw",
    label: () => "Net MW",
    group: "net",
    align: "right",
    render: (r) => formatMw(r.netMw),
    cellClass: (r) => cn("font-medium", signClass(r.netMw)),
  },
  netVwapEur: {
    id: "netVwapEur",
    label: () => "Net €/MWh",
    group: "net",
    align: "right",
    render: (r) => formatPrice(netVwap(r)),
    cellClass: () => "font-medium",
  },
  pnlEur: {
    id: "pnlEur",
    label: () => "P&L €",
    group: "net",
    align: "right",
    render: (r) => {
      const pnl = realizedPnl(r);
      return pnl === null ? "—" : formatPrice(pnl);
    },
    cellClass: (r) => cn("font-medium", signClass(realizedPnl(r))),
  },
  bmNetMw: {
    id: "bmNetMw",
    label: () => "BM MW",
    group: "balancing",
    align: "right",
    render: (r) => formatMw(r.bmNetMw),
    cellClass: (r) => signClass(r.bmNetMw),
  },
  bmNetMwh: {
    id: "bmNetMwh",
    label: () => "BM MWh",
    group: "balancing",
    align: "right",
    render: (r) => formatMw(r.bmNetMwh),
    cellClass: (r) => signClass(r.bmNetMwh),
  },
  bmPriceSource: {
    id: "bmPriceSource",
    label: () => "Source",
    title: () => "Final imported price when available, otherwise estimated from Transelectrica public data.",
    group: "balancing",
    align: "left",
    render: (r) => <BmStatusBadge source={r.bmPriceSource} status={r.bmDataStatus} />,
  },
  bmPositivePriceEur: {
    id: "bmPositivePriceEur",
    label: () => "BM + €/MWh",
    group: "balancing",
    align: "right",
    render: (r) => formatPrice(r.bmPositivePriceEur),
    cellClass: () => "text-muted",
  },
  bmNegativePriceEur: {
    id: "bmNegativePriceEur",
    label: () => "BM - €/MWh",
    group: "balancing",
    align: "right",
    render: (r) => formatPrice(r.bmNegativePriceEur),
    cellClass: () => "text-muted",
  },
  bmAppliedPriceEur: {
    id: "bmAppliedPriceEur",
    label: () => "BM €/MWh",
    title: () => "Applied balancing price for this interval.",
    group: "balancing",
    align: "right",
    render: (r) => formatPrice(r.bmAppliedPriceEur),
    cellClass: () => "font-medium",
  },
  bmPositionPriceEur: {
    id: "bmPositionPriceEur",
    label: () => "DA+ID €/MWh",
    title: () => "Net weighted DA plus ID position price used as the P&L reference.",
    group: "balancing",
    align: "right",
    render: (r) => formatPrice(r.bmPositionPriceEur),
    cellClass: () => "text-muted",
  },
  bmSpreadEur: {
    id: "bmSpreadEur",
    label: () => "Spread €/MWh",
    title: () => "BM price minus DA+ID reference price. P&L also depends on position direction.",
    group: "balancing",
    align: "right",
    render: (r) => formatPrice(bmSpread(r)),
    cellClass: (r) => signClass(r.bmPnlEur),
  },
  bmPnlEur: {
    id: "bmPnlEur",
    label: () => "P&L €",
    title: () => "Economic result of the BM position versus the DA+ID reference price.",
    group: "balancing",
    align: "right",
    render: (r) => formatPrice(r.bmPnlEur),
    cellClass: (r) => cn("font-semibold", signClass(r.bmPnlEur)),
  },
  bmSettlementEur: {
    id: "bmSettlementEur",
    label: () => "Cashflow €",
    title: () => "Gross BM settlement cashflow. This is not P&L.",
    group: "balancing",
    align: "right",
    render: (r) => formatPrice(r.bmSettlementEur),
    cellClass: (r) => signClass(r.bmSettlementEur),
  },
  bmDataStatus: {
    id: "bmDataStatus",
    label: () => "Data",
    title: () => "Balancing data completeness for this interval.",
    group: "balancing",
    align: "left",
    render: (r) => r.bmDataStatus,
    cellClass: (r) =>
      r.bmDataStatus === "missing"
        ? "text-negRed"
        : r.bmDataStatus === "partial"
        ? "text-warnOrange"
        : r.bmDataStatus === "final"
        ? "text-posGreen"
        : "text-muted",
  },
};

const SUMMARY_WHEN_COLLAPSED: Partial<Record<ColGroup, Set<ColId>>> = {
  id: new Set<ColId>(["idTotalMw", "idVwapEur"]),
  balancing: new Set<ColId>(["bmNetMwh", "bmAppliedPriceEur", "bmPnlEur"]),
};

function GridTable({
  rows,
  zone,
  deliveryDate,
}: {
  rows: GridCell[];
  zone: "CET" | "EET";
  deliveryDate: string;
}) {
  const order = useGridColumns((s) => s.order);
  const reorder = useGridColumns((s) => s.reorder);
  const reset = useGridColumns((s) => s.reset);
  const collapsedGroups = useGridColumns((s) => s.collapsedGroups);
  const toggleGroup = useGridColumns((s) => s.toggleGroup);
  const [dragId, setDragId] = useState<ColId | null>(null);
  const [dropTarget, setDropTarget] = useState<{ id: ColId; mode: "before" | "after" } | null>(null);
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);
  const now = useMemo(() => new Date(nowTick), [nowTick]);
  const rowStatus = useMemo(() => {
    const map = new Map<number, { status: GateStatus; minsToClose: number }>();
    for (const r of rows) {
      map.set(r.qhIndex, {
        status: qhGateStatus(deliveryDate, r.qhIndex, now),
        minsToClose: qhMinutesToGateClosure(deliveryDate, r.qhIndex, now),
      });
    }
    return map;
  }, [rows, deliveryDate, now]);

  const visibleOrder = useMemo(() => {
    const collapsed = new Set(collapsedGroups);
    return order.filter((id) => {
      const g = COLS[id].group;
      if (!collapsed.has(g)) return true;
      const summary = SUMMARY_WHEN_COLLAPSED[g];
      return summary?.has(id) ?? false;
    });
  }, [order, collapsedGroups]);

  const groupSpans = useMemo(() => {
    const out: { group: ColGroup; span: number }[] = [];
    for (const id of visibleOrder) {
      const g = COLS[id].group;
      const last = out[out.length - 1];
      if (last && last.group === g) last.span += 1;
      else out.push({ group: g, span: 1 });
    }
    return out;
  }, [visibleOrder]);

  const hasCollapseTarget = (g: ColGroup) => Boolean(SUMMARY_WHEN_COLLAPSED[g]);
  const isCollapsed = (g: ColGroup) => collapsedGroups.includes(g);

  const totals = useMemo(() => computeTotals(rows), [rows]);

  return (
    <div className="min-w-0">
      <div className="sm:hidden">
        <CompactGridView
          rows={rows}
          rowStatus={rowStatus}
          totals={totals}
          zone={zone}
        />
      </div>
      <div className="hidden space-y-2 sm:block">
        <div className="flex items-center gap-3 text-xs text-muted">
          <span>Drag a column header to reorder.</span>
          <button
            type="button"
            onClick={reset}
            className="rounded border border-border px-2 py-0.5 transition-colors hover:border-accent hover:text-fg"
          >
            reset columns
          </button>
        </div>
        <div
          role="region"
          aria-label="Position and P&L data table"
          tabIndex={0}
          className="max-h-[calc(100vh-14rem)] overflow-auto rounded-md border border-border/60 outline-none focus-visible:ring-2 focus-visible:ring-accent"
        >
          <table className="min-w-full border-separate border-spacing-0 font-mono text-[11px]">
          <thead>
            <tr className="text-[10px] uppercase tracking-wider">
              {groupSpans.map((g, i) => {
                const style = GROUP_STYLE[g.group];
                const collapsible = hasCollapseTarget(g.group);
                const collapsed = isCollapsed(g.group);
                return (
                  <th
                    key={`${g.group}-${i}`}
                    colSpan={g.span}
                    className={cn(
                      "px-2 py-1.5 font-semibold text-center border-b border-border/60 bg-bg sticky top-0 z-20",
                      style.text,
                      collapsible && "cursor-pointer select-none hover:bg-border/20"
                    )}
                    onClick={collapsible ? () => toggleGroup(g.group) : undefined}
                    title={collapsible ? (collapsed ? "expand" : "collapse") : undefined}
                  >
                    {style.label}
                    {collapsible && (
                      <span className="ml-1 text-muted">{collapsed ? "▸" : "▾"}</span>
                    )}
                  </th>
                );
              })}
            </tr>
            <tr className="text-muted">
              {visibleOrder.map((id) => {
                const col = COLS[id];
                const isTarget = dropTarget?.id === id;
                return (
                  <th
                    key={id}
                    draggable
                    onDragStart={(e) => {
                      setDragId(id);
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", id);
                    }}
                    onDragOver={(e) => {
                      if (!dragId || dragId === id) return;
                      e.preventDefault();
                      const rect = e.currentTarget.getBoundingClientRect();
                      const mode = e.clientX < rect.left + rect.width / 2 ? "before" : "after";
                      setDropTarget({ id, mode });
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDragLeave={() => setDropTarget(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      if (dragId && dropTarget) reorder(dragId, dropTarget.id, dropTarget.mode);
                      setDragId(null);
                      setDropTarget(null);
                    }}
                    onDragEnd={() => {
                      setDragId(null);
                      setDropTarget(null);
                    }}
                    className={cn(
                      "px-2 py-1.5 font-normal whitespace-nowrap cursor-grab select-none",
                      "sticky top-[1.85rem] z-10 bg-bg",
                      col.align === "right" ? "text-right" : "text-left",
                      dragId === id && "opacity-40",
                      isTarget && dropTarget?.mode === "before" && "border-l-2 border-accent",
                      isTarget && dropTarget?.mode === "after" && "border-r-2 border-accent"
                    )}
                    title={col.title?.(zone)}
                  >
                    {col.label(zone)}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            <tr className="text-[11px] font-semibold">
              {visibleOrder.map((id) => (
                <td
                  key={id}
                  className={cn(
                    "px-2 py-1.5 whitespace-nowrap border-b-2 border-border/60 bg-border/20",
                    COLS[id].align === "right" ? "text-right" : "text-left",
                    renderTotalClass(id, totals)
                  )}
                >
                  {renderTotal(id, totals)}
                </td>
              ))}
            </tr>
            {rows.map((r, idx) => {
              const st = rowStatus.get(r.qhIndex);
              const ctx: RenderCtx = st
                ? { status: st.status, minsToClose: st.minsToClose }
                : {};
              const unflat = Math.abs(r.netMw) > 1e-9;
              const closedUnflat = unflat && st?.status === "closed";
              return (
                <tr
                  key={r.qhIndex}
                  className={cn(
                    "hover:bg-border/10",
                    idx % 2 === 1 && "bg-border/[0.03]",
                    unflat && "bg-negRed/5 outline outline-1 outline-negRed/40"
                  )}
                  title={
                    closedUnflat
                      ? `Closed for trading with open position ${formatMw(
                          r.netMw
                        )} MW — goes to imbalance`
                      : unflat
                      ? `Open position ${formatMw(r.netMw)} MW — not flat yet`
                      : undefined
                  }
                >
                  {visibleOrder.map((id) => {
                    const col = COLS[id];
                    const style = GROUP_STYLE[col.group];
                    return (
                      <Td
                        key={id}
                        className={cn(
                          col.align === "right" ? "text-right" : "",
                          "border-t border-border/30",
                          style.cellBg,
                          col.cellClass?.(r, ctx)
                        )}
                      >
                        {col.render(r, zone, ctx)}
                      </Td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function CompactGridView({
  rows,
  rowStatus,
  totals,
  zone,
}: {
  rows: GridCell[];
  rowStatus: ReadonlyMap<number, { status: GateStatus; minsToClose: number }>;
  totals: GridTotals;
  zone: "CET" | "EET";
}) {
  return (
    <section className="min-w-0 space-y-3" aria-labelledby="grid-intervals-heading">
      <div className="rounded-lg border border-border/60 bg-border/[0.03] p-3">
        <h2 id="grid-intervals-heading" className="text-sm font-semibold text-fg">
          Position and P&amp;L
        </h2>
        <p className="mt-1 text-sm leading-5 text-muted">
          {rows.length} quarter-hour intervals. Open an interval for its DA, IDM and balancing detail.
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-2">
          <CompactMetric label="DA" value={totals.daMwh} unit="MWh" />
          <CompactMetric label="IDM" value={totals.idTotalMwh} unit="MWh" />
          <CompactMetric label="Net DA + IDM" value={totals.netMwh} unit="MWh" />
          <CompactMetric label="Realized P&L" value={totals.pnl} unit="€" price />
          <CompactMetric label="Balancing" value={totals.bmNetMwh} unit="MWh" />
          <CompactMetric label="BM P&L" value={totals.bmPnl} unit="€" price />
        </dl>
      </div>

      <div className="space-y-2">
        {rows.map((row) => {
          const status = rowStatus.get(row.qhIndex);
          return (
            <CompactGridRow
              key={row.qhIndex}
              row={row}
              status={status?.status}
              minsToClose={status?.minsToClose}
              zone={zone}
            />
          );
        })}
      </div>
    </section>
  );
}

function CompactMetric({
  label,
  value,
  unit,
  price = false,
}: {
  label: string;
  value: number | null;
  unit: string;
  price?: boolean;
}) {
  return (
    <div className="min-w-0 rounded-md border border-border/50 bg-bg p-2.5">
      <dt className="text-xs leading-4 text-muted">{label}</dt>
      <dd className={cn("mt-1 break-words font-mono text-base font-semibold tabular-nums", signClass(value))}>
        {price ? formatPrice(value) : formatMw(value)}{" "}
        <span className="text-xs font-normal text-muted">{unit}</span>
      </dd>
    </div>
  );
}

function CompactGridRow({
  row,
  status,
  minsToClose,
  zone,
}: {
  row: GridCell;
  status: GateStatus | undefined;
  minsToClose: number | undefined;
  zone: "CET" | "EET";
}) {
  const idBuy = idBuyMw(row);
  const idSell = idSellMw(row);
  const idNet = idTotalMw(row);
  const pnl = realizedPnl(row);

  return (
    <details className="group min-w-0 rounded-lg border border-border/60 bg-bg open:border-accent/40">
      <summary className="flex min-h-14 cursor-pointer list-none items-center gap-3 rounded-lg px-3 py-2 outline-none marker:hidden focus-visible:ring-2 focus-visible:ring-accent [&::-webkit-details-marker]:hidden">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-semibold text-fg">QH-{row.qhIndex}</span>
            <span className="text-sm text-muted">{qhIntervalLabel(row.qhIndex, zone)}</span>
            <span className="inline-flex items-center gap-1 text-xs text-muted">
              <StatusBadge status={status} minsToClose={minsToClose} />
              {gateStatusLabel(status, minsToClose)}
            </span>
          </div>
          <div className="mt-1 flex min-w-0 flex-wrap gap-x-3 gap-y-1 font-mono text-sm tabular-nums">
            <span className={signClass(row.netMw)}>Net {formatMw(row.netMw)} MW</span>
            <span className={signClass(pnl)}>P&amp;L {formatPrice(pnl)} €</span>
            <span className={bmDataStatusClass(row.bmDataStatus)}>
              BM {row.bmDataStatus}
            </span>
          </div>
        </div>
        <span className="shrink-0 text-lg text-muted transition-transform group-open:rotate-180" aria-hidden="true">
          ▾
        </span>
      </summary>
      <div className="border-t border-border/50 px-3 py-3">
        <KeyValueList
          ariaLabel={`QH-${row.qhIndex} position details`}
          items={[
            {
              key: "da-position",
              label: "DA position",
              value: formatMw(row.daBaseMw),
              unit: "MW",
              context: `@ ${formatPrice(row.daMarketPriceEur ?? row.daPriceEur)} €/MWh`,
              monospace: true,
            },
            {
              key: "qh-buy",
              label: "IDM QH buy",
              value: formatMw(row.qhBuyMw),
              unit: "MW",
              context: `@ ${formatPrice(row.qhBuyVwap)} €/MWh`,
              monospace: true,
            },
            {
              key: "qh-sell",
              label: "IDM QH sell",
              value: formatMw(row.qhSellMw),
              unit: "MW",
              context: `@ ${formatPrice(row.qhSellVwap)} €/MWh`,
              monospace: true,
            },
            {
              key: "ph-buy",
              label: "IDM hourly buy",
              value: formatMw(row.phBuyMw),
              unit: "MW",
              context: `@ ${formatPrice(row.phBuyVwap)} €/MWh`,
              monospace: true,
            },
            {
              key: "ph-sell",
              label: "IDM hourly sell",
              value: formatMw(row.phSellMw),
              unit: "MW",
              context: `@ ${formatPrice(row.phSellVwap)} €/MWh`,
              monospace: true,
            },
            {
              key: "idm-buy",
              label: "IDM total buy",
              value: formatMw(idBuy),
              unit: "MW",
              context: `@ ${formatPrice(idBuyVwap(row))} €/MWh`,
              monospace: true,
            },
            {
              key: "idm-sell",
              label: "IDM total sell",
              value: formatMw(idSell),
              unit: "MW",
              context: `@ ${formatPrice(idSellVwap(row))} €/MWh`,
              monospace: true,
            },
            {
              key: "idm-net",
              label: "IDM net",
              value: formatMw(idNet),
              unit: "MW",
              context: `@ ${formatPrice(idVwap(row))} €/MWh`,
              monospace: true,
            },
            {
              key: "net",
              label: "Net DA + IDM",
              value: formatMw(row.netMw),
              unit: "MW",
              context: `@ ${formatPrice(netVwap(row))} €/MWh`,
              monospace: true,
            },
            {
              key: "realized-pnl",
              label: "Realized P&L",
              value: pnl === null ? null : formatPrice(pnl),
              unit: "€",
              monospace: true,
            },
            {
              key: "bm-position",
              label: "Balancing position",
              value: formatMw(row.bmNetMwh),
              unit: "MWh",
              context: `${row.bmPriceSource} · ${row.bmDataStatus}`,
              monospace: true,
            },
            {
              key: "bm-price",
              label: "Balancing price",
              value: formatPrice(row.bmAppliedPriceEur),
              unit: "€/MWh",
              context: `+ ${formatPrice(row.bmPositivePriceEur)} · − ${formatPrice(row.bmNegativePriceEur)}`,
              monospace: true,
            },
            {
              key: "bm-reference",
              label: "DA + IDM reference",
              value: formatPrice(row.bmPositionPriceEur),
              unit: "€/MWh",
              monospace: true,
            },
            {
              key: "bm-spread",
              label: "Balancing spread",
              value: formatPrice(bmSpread(row)),
              unit: "€/MWh",
              monospace: true,
            },
            {
              key: "bm-pnl",
              label: "Balancing P&L",
              value: formatPrice(row.bmPnlEur),
              unit: "€",
              monospace: true,
            },
            {
              key: "bm-cashflow",
              label: "Balancing cashflow",
              value: formatPrice(row.bmSettlementEur),
              unit: "€",
              monospace: true,
            },
          ]}
        />
      </div>
    </details>
  );
}

function gateStatusLabel(
  status: GateStatus | undefined,
  minsToClose: number | undefined
): string {
  if (status === "open") return "open";
  if (status === "closing") {
    return minsToClose === undefined ? "closing" : `closing in ${Math.max(0, Math.round(minsToClose))}m`;
  }
  if (status === "closed") return "closed";
  return "status unavailable";
}

function bmDataStatusClass(status: GridCell["bmDataStatus"]): string {
  return status === "missing"
    ? "text-negRed"
    : status === "partial"
      ? "text-warnOrange"
      : status === "final"
        ? "text-posGreen"
        : "text-muted";
}

function renderTotal(id: ColId, t: GridTotals): React.ReactNode {
  switch (id) {
    case "qh":
      return "TOTAL";
    case "interval":
      return "(MWh / wavg)";
    case "daPriceEur":
      return formatPrice(t.daPriceEurWeighted);
    case "daBaseMw":
      return formatMw(t.daMwh);
    case "qhBuyMw":
      return formatMw(t.qhBuyMwh);
    case "qhBuyVwap":
      return formatPrice(t.qhBuyVwap);
    case "qhSellMw":
      return formatMw(t.qhSellMwh);
    case "qhSellVwap":
      return formatPrice(t.qhSellVwap);
    case "phBuyMw":
      return formatMw(t.phBuyMwh);
    case "phBuyVwap":
      return formatPrice(t.phBuyVwap);
    case "phSellMw":
      return formatMw(t.phSellMwh);
    case "phSellVwap":
      return formatPrice(t.phSellVwap);
    case "idTotalMw":
      return formatMw(t.idTotalMwh);
    case "idVwapEur":
      return formatPrice(t.idVwap);
    case "netMw":
      return formatMw(t.netMwh);
    case "netVwapEur":
      return formatPrice(t.netVwap);
    case "pnlEur":
      return formatPrice(t.pnl);
    case "bmNetMw":
      return formatMw(t.bmNetMwh);
    case "bmNetMwh":
      return formatMw(t.bmNetMwh);
    case "bmPriceSource":
      return t.bmMissingRows > 0 ? `${t.bmMissingRows} missing` : t.bmPricedRows > 0 ? "priced" : "—";
    case "bmPositivePriceEur":
    case "bmNegativePriceEur":
    case "bmAppliedPriceEur":
    case "bmPositionPriceEur":
    case "bmSpreadEur":
    case "bmDataStatus":
      return "—";
    case "bmPnlEur":
      return formatPrice(t.bmPnl);
    case "bmSettlementEur":
      return formatPrice(t.bmSettlement);
    default:
      return null;
  }
}

function renderTotalClass(id: ColId, t: GridTotals): string {
  switch (id) {
    case "daBaseMw":
      return signClass(t.daMwh);
    case "qhBuyMw":
      return signClass(t.qhBuyMwh);
    case "qhSellMw":
      return signClass(t.qhSellMwh);
    case "phBuyMw":
      return signClass(t.phBuyMwh);
    case "phSellMw":
      return signClass(t.phSellMwh);
    case "idTotalMw":
      return signClass(t.idTotalMwh);
    case "netMw":
      return signClass(t.netMwh);
    case "pnlEur":
      return signClass(t.pnl);
    case "bmNetMw":
    case "bmNetMwh":
      return signClass(t.bmNetMwh);
    case "bmPriceSource":
      return t.bmMissingRows > 0 ? "text-negRed" : "text-muted";
    case "bmSettlementEur":
      return signClass(t.bmSettlement);
    case "bmPnlEur":
      return signClass(t.bmPnl);
    default:
      return "";
  }
}

function BmStatusBadge({
  source,
  status,
}: {
  source: GridCell["bmPriceSource"];
  status: GridCell["bmDataStatus"];
}) {
  const label = source === "final" ? "final" : source === "estimated" ? "est" : "—";
  const cls =
    source === "final"
      ? "border-posGreen/50 bg-posGreen/10 text-posGreen"
      : source === "estimated"
      ? "border-warnOrange/50 bg-warnOrange/10 text-fg"
      : "border-border text-muted";
  return (
    <span
      className={cn("inline-flex min-w-10 justify-center rounded border px-1.5 py-0.5 text-[10px]", cls)}
      title={status}
    >
      {label}
    </span>
  );
}

function StatusBadge({
  status,
  minsToClose,
}: {
  status: GateStatus | undefined;
  minsToClose?: number;
}) {
  if (!status) return null;
  if (status === "open") {
    return (
      <span
        className="inline-block w-2 h-2 rounded-full bg-posGreen"
        title="gate open"
      />
    );
  }
  if (status === "closing") {
    const mins = minsToClose !== undefined ? Math.max(0, Math.round(minsToClose)) : null;
    return (
      <span
        className="inline-flex items-center gap-1 text-warnOrange"
        title="gate closes within 1 hour"
      >
        <span className="w-2 h-2 rounded-full bg-warnOrange" />
        {mins !== null ? `${mins}m` : ""}
      </span>
    );
  }
  return (
    <span
      className="inline-block w-2 h-2 rounded-full bg-negRed"
      title="gate closed — goes to imbalance"
    />
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("px-2 py-1.5 font-normal whitespace-nowrap", className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-2 py-1 whitespace-nowrap", className)}>{children}</td>;
}
